import React, {useMemo} from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { Grid } from '~/components';
import { useForm } from '~/hooks';
import { useLoadSection, useSectionDetail } from '~/hooks/useSection';
import sectionTypes from '../sectionTypes';
import DeedForm from './DeedForm';

const initialState = {
    id: 0,
    sectionId: 0,
    sectionDetails: [],
    titleOpinionId: 0,
    sectionType: sectionTypes.FILE,
    selectedDeed: {},
    editDeed: false,
    modalOpen: false
};

const DeedInfo = ({ titleOpinionId, readonly = false, hasFileNumber }) => {
    const _readonly = useMemo(() => readonly || !hasFileNumber, [readonly, hasFileNumber]);

    const { onLoad } = useLoadSection(sectionTypes.FILE, titleOpinionId);

    const { state, fieldActions } = useForm({ initialState, onLoad });
    
    const {   
        saveDetail, 
        deleteDetail 
    } = useSectionDetail(sectionTypes.FILE, state.values.sectionId, titleOpinionId);
    
    const { sectionDetails } = state.values;

    const openModal = () => fieldActions.onChange(true, 'modalOpen');
    const closeModal = async () => {
        await fieldActions.onChange({}, 'selectedDeed');
        await fieldActions.onChange(false, 'modalOpen');
    };

    const saveDeed = async values => {
        const {
            grantors,
            grantees,
            trustee,
            purchasePrice,
            dated,
            recordedOn,
            book,
            page,
            countyRegistry,
            copyOfDeed
        } = values;

        const data = {
            sectionId: state.values.sectionId,
            grantors,
            grantees,
            trustee,
            purchasePrice,
            dated,
            recordedOn,
            book,
            page,
            countyRegistry,
            documents: copyOfDeed
        };

        await saveDetail(data, values.sectionDetailId);
        closeModal();
    };

    const editDeed = async (data) => {
        await fieldActions.onChange(data, 'selectedDeed');
        await fieldActions.onChange(true, 'editDeed');
        openModal();
    };

    const addDeed = async () => {
        await fieldActions.onChange(false, 'editDeed');
        openModal();
    };

    const deleteDeed = id => deleteDetail(id);

    return (
        <>
            <DeedGrid className='col-span-12' details={sectionDetails} onEdit={editDeed} onDelete={deleteDeed} readonly={_readonly}/>
            {_readonly || (
                <div className='col-span-12 flex items-center'>
                    <button
                        className='rounded-full py-2 px-4 bg-primary-60 text-white text-sm font-medium focus:outline-none hover:bg-primary-50'
                        onClick={addDeed}
                    >
                        Add Deed
                    </button>
                </div>
            )}
            <DeedForm 
                isOpen={state.values.modalOpen} 
                closeModal={closeModal} 
                saveDeed={saveDeed}
                readonly={_readonly}
                deed={state.values.selectedDeed}
                edit={state.values.editDeed}
            />
        </>
    );
};

export default DeedInfo;

const headerClassName = 'bg-gray-50 py-2 px-4 border-b text-left';
const props = {
    className: 'py-1 px-4 border-t text-xs text-neutral-70'
};

function DeedGrid({ details, className, onEdit, readonly = false, onDelete }) {
    const columns = [
        {
            title: 'Deeds',
            cell: ({ data }) => {
                return (
                    <>{`Deed #${data.index + 1}`}</>
                );
            },
            canSort: false,
            headerClassName,
            props
        },
        {
            headerClassName,
            props,
            key: 'actions',
            canSort: false,
            title: '',
            cell: ({ data }) => {

                return readonly 
                    ? (
                        <span className="flex justify-end h-8">
                            <button className="focus:outline-none" onClick={() => onEdit(data)}>
                                <AiOutlineSearch />
                            </button>
                        </span>
                    ) : 
                    (
                        <span className="flex justify-end h-8">
                            <button className="mr-5 focus:outline-none"
                                onClick={() => onEdit(data)}
                            >
                                <FaPencilAlt />
                            </button>
                            <button className="focus:outline-none"
                                onClick={() => onDelete(data.sectionDetailId)}>
                                <FaTrash />
                            </button>
                        </span>
                    );
            }
        }
    ];

    const deeds = Array.isArray(details)
        ? details.map((d,i) => ({ ...d, index: i }) )
        : [];

    return (
        <Grid className={className} columns={columns} data={deeds}/>
    );
}
